import type { ComposerWidget } from '@safetyhub/shared';
import { isRepeatTableNonColumnChild } from '@safetyhub/report-composer';

/**
 * 표형(displayVariant:'table') repeat의 **열 이동** 목표 인덱스 계산 — 순수 함수.
 *
 * 왜 별도 계산이 필요한가: 열 순서는 repeat children 순서와 같은 축이지만, children에는 **열이
 * 되지 않는 비시각 위젯**(hideButton modalTrigger 등 — `isRepeatTableNonColumnChild`)이 섞여
 * 있다. 그래서 형제 한 칸 이동(rc `moveWidgetInSiblings(ForView)`)을 그대로 쓰면 비시각 위젯과
 * 자리만 바꾸고 **보이는 열 순서는 그대로**가 된다 — 저작자에겐 "버튼이 안 먹는" 것으로 보인다.
 * 이 함수는 그 사이의 비시각 위젯을 건너뛰어 **인접한 열**의 표시 인덱스를 돌려준다.
 *
 * @param ordered 형제 위젯을 **표시 순서로 정렬한** 배열. 호출부가 뷰를 반영해 넘긴다
 *   (base 뷰=children 그대로, 그 외=`orderSiblingsForView(children, view)`) — 캔버스 표의
 *   열 도출(`composerWidgetTreeRender` tableMode)과 같은 규칙이어야 헤더↔이동이 정합한다.
 * @param id 옮길 열 위젯 id
 * @param dir 'up'=왼쪽 열과 교환, 'down'=오른쪽 열과 교환
 * @returns rc `moveWidgetToIndexForView(tree, id, <이 값>, view)`에 그대로 넘길 **표시 인덱스**.
 *   경계(첫/마지막 열)이거나 id가 열이 아니면 `null`(무변경 — 호출부가 no-op 처리).
 *
 * ⚠️ 반환값은 "인접 열이 현재 놓인 표시 인덱스"다. `reorderSiblingsForView`가 **먼저 제거하고
 * 그 인덱스에 삽입**하는 splice 의미라, 사이에 비시각 위젯이 몇 개 있든 네 방향(첫 열 ↓,
 * 중간 열 ↑/↓, 끝 열 ↑) 모두 정확히 인접 열과 교환된다(spec의 표 참조).
 */
export function repeatColumnMoveTargetIndex(
  ordered: ComposerWidget[],
  id: string,
  dir: 'up' | 'down',
  // "열" 판정 주입 — 캔버스 열 도출(repeatTableColumnChildren: 비시각+현재 뷰 숨김 제외)과 같은
  // 집합을 호출부가 전달한다(미전달=비시각만 제외, 하위호환). 숨김 위젯과 자리만 바꾸는 무의미
  // 이동을 막고, 목록·이동·제거가 한 집합을 공유하게 한다.
  isColumnCandidate: (w: ComposerWidget) => boolean = (w) => !isRepeatTableNonColumnChild(w),
): number | null {
  // 표시 순서 배열에서 "열"이 놓인 인덱스만 추린다(캔버스 열 도출과 동일 규칙 — 판정 주입).
  const columnIndices: number[] = [];
  for (let i = 0; i < ordered.length; i += 1) {
    if (isColumnCandidate(ordered[i])) columnIndices.push(i);
  }
  const pos = columnIndices.findIndex((i) => ordered[i].id === id);
  if (pos < 0) return null; // 열이 아님(비시각 위젯이거나 형제가 아님)
  const targetPos = dir === 'up' ? pos - 1 : pos + 1;
  if (targetPos < 0 || targetPos >= columnIndices.length) return null; // 경계 — 무변경
  return columnIndices[targetPos];
}

/**
 * 표형 repeat에서 그 위젯이 **열 이동 가능한지**(경계 판정) — ↑/↓ 버튼 disabled 계산용.
 * `repeatColumnMoveTargetIndex`와 같은 규칙을 쓰므로 버튼 활성 상태와 실제 동작이 어긋나지 않는다.
 */
export function canMoveRepeatColumn(
  ordered: ComposerWidget[],
  id: string,
  dir: 'up' | 'down',
  isColumnCandidate?: (w: ComposerWidget) => boolean,
): boolean {
  return repeatColumnMoveTargetIndex(ordered, id, dir, isColumnCandidate) !== null;
}
